import { AppProps } from 'next/app';
import Head from 'next/head';
import './styles.scss';
import { FC } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import NextNProgress from 'nextjs-progressbar';
import {
  AlertProvider,
  AuthProvider,
  StyledSnackbarProvider,
} from '@web-kc/core/providers';
import { queryClient } from '@web-kc/core/api';
import { gilroy, daxProBold, themeKc } from '@web-kc/core/theme';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { AppCacheProvider } from '@mui/material-nextjs/v16-pagesRouter';
import {
  AlertHeader,
  AppErrorFallback,
  Footer,
  HeaderKC,
  PageErrorFallback,
  PopupGateProvider,
} from '@web-kc/shared/components';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import 'dayjs/locale/kk';
import { useRouter } from 'next/router';
import { SharedI18nProvider } from '@web-shared/core/i18n';
import * as Sentry from '@sentry/nextjs';
import clsx from 'clsx';
import { installTranslationCrashGuard } from '@web-kc/shared/helpers';

installTranslationCrashGuard();

const KcApp: FC<AppProps> = (props) => {
  const { Component, pageProps } = props;
  const { t } = useTranslation('common');
  const { locale = 'ru' } = useRouter();

  dayjs.locale(locale);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => scope.setTag('boundary', 'app')}
      fallback={<AppErrorFallback />}
    >
      <AppCacheProvider {...props}>
        <Head>
          <title>{t('common:tabs.defaultPrefix_kc')}</title>
          <meta
            property="og:title"
            content={t('common:tabs.defaultPrefix_kc')}
            key="title"
          />
          <link rel="icon" type="image/x-icon" href="/favicon.svg" />
        </Head>

        <style jsx global>{`
          :root {
            --font-gilroy: ${gilroy.style.fontFamily};
            --font-daxPro-bold: ${daxProBold.style.fontFamily};
          }
          html {
            font-family: ${gilroy.style.fontFamily}, sans-serif;
          }
        `}</style>
        <Box
          component="main"
          display="flex"
          flexDirection="column"
          minHeight="100vh"
          className={clsx('app', gilroy.variable, daxProBold.variable)}
        >
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={themeKc}>
              <SharedI18nProvider locale={locale} translate={t}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={locale}
                >
                  <StyledSnackbarProvider>
                    <AuthProvider>
                      <AlertProvider>
                        <AlertHeader />
                        <HeaderKC />
                        <NextNProgress
                          height={3}
                          color={themeKc.palette.primary.main}
                          options={{ showSpinner: false }}
                        />

                        <PopupGateProvider>
                          <Sentry.ErrorBoundary
                            beforeCapture={(scope) =>
                              scope.setTag('boundary', 'page')
                            }
                            fallback={<PageErrorFallback />}
                          >
                            <Component {...pageProps} />
                          </Sentry.ErrorBoundary>

                          <Footer />
                        </PopupGateProvider>
                      </AlertProvider>
                    </AuthProvider>
                  </StyledSnackbarProvider>
                </LocalizationProvider>
              </SharedI18nProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </Box>
      </AppCacheProvider>
    </Sentry.ErrorBoundary>
  );
};

export default appWithTranslation(KcApp);
